import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Flex } from '@chakra-ui/react';
import { BICtoBankInfo } from '@shinetools/iban-bban-bic';
import { printFormat } from 'iban';

import {
  FeatureFilterField,
  FeatureName,
  UserFilterValues,
} from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Loader from 'components/Loader';
import TrackView from 'features/Analytics/TrackView';
import { throwIfErrorPresent } from 'features/Onboarding/utils';
import { formatAddress } from 'helpers/address';
import * as Layout from 'layouts/ContentLayout';

import * as routes from '../../routes';
import { useUserProfile } from './hooks/useUserProfile';
import locales from './locales';

const Profile: FC = () => {
  const { error, loading, user } = useUserProfile();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();

  const canEditPersonalAddress = canAccessFeature({
    name: FeatureName.UserProfileWriteAddress,
  });
  const canAccessBankDetails = canAccessFeature({
    name: FeatureName.RequestExpenseclaims,
    where: {
      or: [
        {
          field: FeatureFilterField.User,
          values: [UserFilterValues.Self],
        },
      ],
    },
  });

  throwIfErrorPresent(error);

  if (loading || !user) {
    return <Loader />;
  }

  const { email, firstName, lastName } = user;
  const currentAddress = formatAddress(user);
  const recipient = user.bankDetails
    ? `${BICtoBankInfo(user.bankDetails.swiftBic).name} · ${printFormat(
        user.bankDetails.iban,
      )}`
    : locales.missingBankDetails;

  return (
    <Layout.Body>
      <TrackView pageName="Profile" />

      <Layout.Header title={`${firstName} ${lastName}`} />

      <Flex direction="column" gap="space-32">
        <SunshineCard.Group>
          <SunshineCard as={Link} title={locales.email} to={routes.editEmail}>
            <SunshineCard.Slot name="aside">
              <SquircleIcon name="mail" />
            </SunshineCard.Slot>

            <SunshineCard.Content>
              <SunshineCard.Text>{email}</SunshineCard.Text>
            </SunshineCard.Content>

            <SunshineCard.Slot name="action">
              <SunshineCard.SunshineIcon name="edit" />
            </SunshineCard.Slot>
          </SunshineCard>

          {canEditPersonalAddress ? (
            <SunshineCard
              as={Link}
              title={locales.personalAddress}
              to={routes.editAddress}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="home" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Text>{currentAddress}</SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="edit" />
              </SunshineCard.Slot>
            </SunshineCard>
          ) : null}
          {canAccessBankDetails ? (
            <SunshineCard
              as={Link}
              title={locales.bankingDetails}
              to={routes.editBankDetails}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="transfer" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Text>{recipient}</SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="edit" />
              </SunshineCard.Slot>
            </SunshineCard>
          ) : null}
        </SunshineCard.Group>
        <SunshineCard
          alignItems="center"
          as="a"
          href="https://shine.fr/lp/cgu-ep-en-clair/"
          rel="noopener noreferrer"
          target="_blank"
          title={locales.terms}
        >
          <SunshineCard.Slot name="aside">
            <SquircleIcon name="file" />
          </SunshineCard.Slot>

          <SunshineCard.Slot name="action">
            <SunshineCard.SunshineIcon name="chevron-right" />
          </SunshineCard.Slot>
        </SunshineCard>
      </Flex>
    </Layout.Body>
  );
};

export default Profile;
